// src/constants/pageSEO.js

import constants from "./constants";

const pageSEO = {
  // home page SEO
  home: () => ({
    title: `${constants.BROKERAGE_NAME} - Professional Real Estate Services`,
    description: `${constants.BROKERAGE_NAME} - Professional real estate services to help you find your dream home. We offer property search, market analysis, and expert realtor services.`,
    keywords:
      "real estate, realtor, property, home, house, real estate services",
    path: "/",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: constants.BROKERAGE,
      url:
        constants.ENVIRONMENT === "production"
          ? `https://${constants.DOMAIN}`
          : `http://${constants.URL}`,
      logo: "/logo.png",
      contactPoint: {
        "@type": "ContactPoint",
        telephone: constants.AGENT_NUMBER,
        contactType: "customer service",
      },
    },
  }),

  // calculator page SEO
  calculator: () => ({
    title: "Mortgage Calculator",
    description:
      "Use our mortgage calculator to estimate your monthly payments, total interest costs, and affordability. Make informed financial decisions.",
    keywords:
      "mortgage calculator, loan calculator, monthly payments, interest, home buying budget",
    path: "/calculator",
  }),

  // listing page SEO
  listing: (listing) => ({
    title: `${listing?.address || 'Property'} - ${listing?.location || 'Location'}`,
    description: `${listing?.bedrooms || 'N/A'} bedroom ${listing?.bathrooms || 'N/A'} bathroom ${listing?.propertyType || 'property'} located in ${listing?.location || 'this area'}, priced at ${listing?.price || 'Contact for price'}.`,
    keywords: `${listing?.location || 'property'}, ${listing?.propertyType || 'real estate'}, ${listing?.bedrooms || 'N/A'} bedroom, ${listing?.bathrooms || 'N/A'} bathroom, property`,
    path: `/listing/${listing?.id || ''}`,
    image: listing?.mainImage,
    type: "product",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "RealEstateListing",
      name: listing?.address || 'Property',
      description: listing?.description || 'Property listing',
      image: listing?.mainImage,
      offers: {
        "@type": "Offer",
        priceCurrency: "CAD",
        price: listing?.price ? listing?.price?.replace(/[^\d.]/g, "") : "",
        availability: "https://schema.org/InStock",
      },
    },
  }),

  // listings page SEO
  listings: () => ({
    title: "Property Listings",
    description:
      "Browse our curated selection of properties, including condos, single-family homes, and townhouses in Toronto and surrounding areas.",
    keywords:
      "property listings, real estate, homes for sale, Toronto properties, condos, houses",
    path: "/listings",
  }),

  // about page SEO
  about: () => ({
    title: "About Us",
    description: constants.ABOUT,
    keywords:
      "about us, real estate agent, real estate pro, real estate professional, trusted realtor",
    path: "/about",
  }),

  // evaluation page SEO
  evaluation: () => ({
    title: "Property Evaluation",
    description:
      "Get a free market evaluation of your property. Understand your property's value in the current market and make informed buying or selling decisions.",
    keywords:
      "property evaluation, home value, market analysis, real estate assessment",
    path: "/evaluation",
  }),

  // reports page SEO
  reports: () => ({
    title: "Real Estate Market Reports",
    description:
      "Latest real estate market trends, analysis, and forecasts. Learn about housing price trends, inventory levels, and market dynamics in different areas.",
    keywords:
      "real estate market, market reports, price trends, market analysis, real estate forecast",
    path: "/real-estate-reports",
  }),

  // contact page SEO
  contact: () => ({
    title: "Contact Us",
    description: `${constants.AGENT_NAME}, the real estate agent for professional advice on buying, selling, or investing in real estate.`,
    keywords: "contact, real estate agent, best realtor, property consultation, trusted realtor",
    path: "/contact",
  }),

  // agents page SEO
  agents: () => ({
    title: "Our Real Estate Agents",
    description:
      "Meet our team of professional real estate agents. Each agent brings extensive experience and expertise to help you buy or sell your property.",
    keywords:
      "real estate agents, realtors, property experts, real estate professionals",
    path: "/agents",
  }),

  // agent page SEO
  agent: (agent) => ({
    title: `${agent.name} - ${agent.title} | ${constants.BROKERAGE}`,
    description: `Meet ${agent.name}, our professional ${agent.title}. ${agent.bio ? agent.bio.substring(0, 150) + "..." : ""
      }`,
    keywords: `${agent.name}, real estate agent, real estate pro, real estate professional, trusted realtor`,
    path: `/agents/${agent.name.toLowerCase().replace(/\s+/g, "-")}`,
    image: agent.img,
    type: "profile",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      name: agent.name,
      image: agent.img,
      description: agent.bio,
      telephone: agent.number,
      email: agent.email,
      worksFor: {
        "@type": "RealEstateOrganization",
        name: constants.BROKERAGE,
      },
    },
  }),

  // commercial page SEO
  commercial: () => ({
    title: "Commercial Real Estate",
    description:
      "Browse our commercial property listings, including office spaces, retail storefronts, and industrial properties. Professional commercial real estate services.",
    keywords:
      "commercial real estate, office space, retail space, investment property, commercial property",
    path: "/commercial",
  }),

  // seller guide page SEO
  sellerGuide: () => ({
    title: "Seller's Guide",
    description:
      "Professional guide to selling your home, providing tips on property preparation, pricing strategy, and marketing techniques to get the best price.",
    keywords:
      "home selling, seller's guide, property sales, real estate market, pricing",
    path: "/selling-guide",
  }),

  // buyer guide page SEO
  buyerGuide: () => ({
    title: "Buyer's Guide",
    description:
      "Comprehensive guide to buying a home, helping you understand the buying process, financing options, and factors to consider.",
    keywords:
      "home buying, buyer's guide, real estate, buying process, mortgage",
    path: "/buying-guide",
  }),

  // location page SEO
  location: (location) => ({
    title: `${location.name} Real Estate and Properties`,
    description: `Browse the latest properties, real estate market trends, and community information in ${location.name}. Learn about housing prices, schools, and local amenities in ${location.name}.`,
    keywords: `${location.name}, real estate, properties, homes for sale, ${location.name} community, ${location.name} housing market`,
    path: `/location/${location.slug}`,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Place",
      name: location.name,
      description: `Browse the latest properties, real estate market trends, and community information in ${location.name}.`,
    },
  }),

  //city page SEO
  city: (city) => ({
    title: `Top Realtor in ${city.name}`,
    description: `Expert realtor in ${city.name}. Buy or sell with confidence. Free home valuation – call today!`,
    keywords: `realtor in ${city.name}, best realtor ${city.name}, homes for sale in ${city.name}, ${city.name} real estate agent, sell my home in ${city.name}, ${city.name} home valuation, buy house in ${city.name}, top real estate agent ${city.name}`,
    path: `realtor-in-${city.slug}`,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "City",
      name: `Trusted Realtor ${city.name}`,
      url: constants.ENVIRONMENT === "production"
        ? `https://${constants.DOMAIN}/realtor-in-${city.slug}`
        : `http://${constants.URL}/realtor-in-${city.slug}`,
      areaServed: city.name,
      telephone: "+1-416-555-1234",
      address: {
        "@type": "PostalAddress",
        addressLocality: city.name,
        addressRegion: "ON",
        addressCountry: "CA",
      },
    },
  })
};

export default pageSEO;
